// Serviço para gerenciar pacotes no Supabase
import { supabase } from '../lib/supabase';

export interface Pacote {
  id: string;
  nome: string;
  descricao: string;
  valorMensal: number;
  valorAnual: number;
  usuarios: number;
  storageDados: string;
  storageArquivos: string;
  features: string[];
  ativo: boolean;
  destacado: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PacoteDB {
  id: string;
  nome: string;
  descricao: string | null;
  valor_mensal: number;
  valor_anual: number;
  usuarios: number;
  storage_dados: string | null;
  storage_arquivos: string | null;
  features: any; // JSONB
  ativo: boolean;
  destacado: boolean;
  created_at: string;
  updated_at: string;
}

function pacoteDBToPacote(pacoteDB: PacoteDB): Pacote {
  return {
    id: pacoteDB.id,
    nome: pacoteDB.nome,
    descricao: pacoteDB.descricao || '',
    valorMensal: Number(pacoteDB.valor_mensal),
    valorAnual: Number(pacoteDB.valor_anual),
    usuarios: pacoteDB.usuarios,
    storageDados: pacoteDB.storage_dados || '',
    storageArquivos: pacoteDB.storage_arquivos || '',
    features: Array.isArray(pacoteDB.features) ? pacoteDB.features : [],
    ativo: pacoteDB.ativo,
    destacado: pacoteDB.destacado,
    createdAt: pacoteDB.created_at,
    updatedAt: pacoteDB.updated_at,
  };
}

// Buscar todos os pacotes
export async function getPacotes(): Promise<Pacote[]> {
  try {
    const { data, error } = await (supabase as any)
      .from('pacotes')
      .select('*')
      .order('valor_mensal', { ascending: true });

    if (error) throw error;

    return (data || []).map(pacoteDBToPacote);
  } catch (error) {
    console.error('[pacotesService] Erro ao buscar pacotes:', error);
    return [];
  }
}

// Buscar pacote por ID
export async function getPacoteById(id: string): Promise<Pacote | null> {
  try {
    const { data, error } = await (supabase as any)
      .from('pacotes')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    if (!data) return null;

    return pacoteDBToPacote(data);
  } catch (error) {
    console.error('[pacotesService] Erro ao buscar pacote:', error);
    return null;
  }
}

// Criar pacote
export async function createPacote(pacote: Omit<Pacote, 'id' | 'createdAt' | 'updatedAt'>): Promise<Pacote | null> {
  try {
    const pacoteDB = {
      nome: pacote.nome,
      descricao: pacote.descricao || null,
      valor_mensal: pacote.valorMensal,
      valor_anual: pacote.valorAnual,
      usuarios: pacote.usuarios,
      storage_dados: pacote.storageDados || null,
      storage_arquivos: pacote.storageArquivos || null,
      features: pacote.features || [],
      ativo: pacote.ativo,
      destacado: pacote.destacado,
    };

    const { data, error } = await (supabase as any)
      .from('pacotes')
      .insert(pacoteDB)
      .select()
      .single();

    if (error) throw error;
    if (!data) return null;

    return pacoteDBToPacote(data);
  } catch (error) {
    console.error('[pacotesService] Erro ao criar pacote:', error);
    throw error;
  }
}

// Atualizar pacote
export async function updatePacote(id: string, pacote: Partial<Omit<Pacote, 'id' | 'createdAt' | 'updatedAt'>>): Promise<Pacote | null> {
  try {
    const updateData: any = {};

    if (pacote.nome !== undefined) updateData.nome = pacote.nome;
    if (pacote.descricao !== undefined) updateData.descricao = pacote.descricao || null;
    if (pacote.valorMensal !== undefined) updateData.valor_mensal = pacote.valorMensal;
    if (pacote.valorAnual !== undefined) updateData.valor_anual = pacote.valorAnual;
    if (pacote.usuarios !== undefined) updateData.usuarios = pacote.usuarios;
    if (pacote.storageDados !== undefined) updateData.storage_dados = pacote.storageDados || null;
    if (pacote.storageArquivos !== undefined) updateData.storage_arquivos = pacote.storageArquivos || null;
    if (pacote.features !== undefined) updateData.features = pacote.features;
    if (pacote.ativo !== undefined) updateData.ativo = pacote.ativo;
    if (pacote.destacado !== undefined) updateData.destacado = pacote.destacado;

    const { data, error } = await (supabase as any)
      .from('pacotes')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    if (!data) return null;

    return pacoteDBToPacote(data);
  } catch (error) {
    console.error('[pacotesService] Erro ao atualizar pacote:', error);
    throw error;
  }
}

// Deletar pacote
export async function deletePacote(id: string): Promise<boolean> {
  try {
    const { error } = await (supabase as any)
      .from('pacotes')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('[pacotesService] Erro ao deletar pacote:', error);
    throw error;
  }
}

